import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  selectAttendanceStack,
  parseAttendanceTime,
} from 'services/attendance/attendanceStatus';
import './DutyBar.css';

const parseSince = (since) => parseAttendanceTime(since);

const formatElapsed = (ms) => {
  if (ms == null || ms < 0) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const DutyBar = ({ stack }) => {
  const [now, setNow] = useState(Date.now());
  const [blocked, setBlocked] = useState(false);

  const primary = useMemo(
    () => (stack.length ? stack[stack.length - 1] : null),
    [stack]
  );
  const outers = useMemo(
    () => (stack.length > 1 ? stack.slice(0, -1) : []),
    [stack]
  );

  const visit = useMemo(() => stack.find((e) => e.level === 'visit') || null, [stack]);
  const shift = useMemo(() => stack.find((e) => e.level === 'shift') || null, [stack]);

  useEffect(() => {
    if (!stack.length) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stack.length]);

  useEffect(() => {
    if (!visit) setBlocked(false);
  }, [visit]);

  if (!stack.length || !primary) return null;

  const primarySinceMs = parseSince(primary.since);
  const primaryClock = formatElapsed(primarySinceMs ? now - primarySinceMs : null);

  const renderAction = (entry, extraClass = '') => {
    if (entry.level === 'visit' || entry.level === 'darwin' || !entry.checkOutPath) {
      return null;
    }
    return (
      <Link to={entry.checkOutPath} className={`duty-bar__btn ${extraClass}`.trim()}>
        {entry.checkOutLabel}
      </Link>
    );
  };

  const renderLabel = (entry, clock) => {
    const text = (
      <>
        {entry.label}
        {clock ? (
          <span className="duty-bar__clock"> &middot; {clock}</span>
        ) : null}
      </>
    );
    const canLink = entry.level === 'shift' && entry.checkOutPath;
    if (canLink) {
      return (
        <Link to={entry.checkOutPath} className="duty-bar__label duty-bar__label--link">
          {text}
        </Link>
      );
    }
    return <span className="duty-bar__label">{text}</span>;
  };

  const renderOuterAction = (entry) => {
    if (entry.level === 'shift' && visit) {
      return (
        <button
          type="button"
          className="duty-bar__btn duty-bar__btn--ghost"
          onClick={() => setBlocked(true)}
        >
          {entry.checkOutLabel}
        </button>
      );
    }
    if (entry.level === 'darwin' || !entry.checkOutPath) {
      return null;
    }
    return (
      <Link to={entry.checkOutPath} className="duty-bar__btn duty-bar__btn--ghost">
        {entry.checkOutLabel}
      </Link>
    );
  };

  return (
    <div className="duty-bar-wrap" role="status">
      <div className="duty-bar duty-bar--primary">
        <span className="duty-bar__state">
          <span className="duty-bar__live" aria-hidden="true" />
          {renderLabel(primary, primaryClock)}
        </span>
        {renderAction(primary)}
      </div>

      {outers
        .slice()
        .reverse()
        .map((outer) => {
          const outerSinceMs = parseSince(outer.since);
          const outerClock = formatElapsed(outerSinceMs ? now - outerSinceMs : null);
          return (
            <div key={outer.level} className="duty-bar duty-bar--outer">
              <span className="duty-bar__sub">
                {outer.level === 'shift' || outer.kind === 'journey'
                  ? outer.label
                  : `within ${outer.label}`}
                {outerClock ? (
                  <span className="duty-bar__clock"> &middot; {outerClock}</span>
                ) : null}
              </span>
              {renderOuterAction(outer)}
            </div>
          );
        })}

      {blocked && visit && shift ? (
        <div className="duty-bar__guard" role="alert">
          <span className="duty-bar__guard-msg">
            You&rsquo;re still checked in at <b>{visit.name}</b>. Check out of the site
            from its page before ending the journey.
          </span>
          <button
            type="button"
            className="duty-bar__btn duty-bar__btn--ghost"
            onClick={() => setBlocked(false)}
          >
            Dismiss
          </button>
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => ({ stack: selectAttendanceStack(state) });

export default connect(mapStateToProps)(DutyBar);
